import { useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams, type Location, type NavigateFunction } from 'react-router';

const PARAM = 'carte';

type CardSheetLocation = Pick<Location<unknown>, 'pathname' | 'search' | 'state'>;

export function openCardSheet(navigate: NavigateFunction, location: CardSheetLocation, characterId: number) {
  const next = new URLSearchParams(location.search);
  next.set(PARAM, String(characterId));
  const state = location.state !== null && typeof location.state === 'object' ? location.state : {};
  return navigate(
    { pathname: location.pathname, search: `?${next}` },
    { preventScrollReset: true, state: { ...state, cardSheet: true } },
  );
}

export function closeCardSheet(navigate: NavigateFunction, location: CardSheetLocation) {
  const next = new URLSearchParams(location.search);
  if (!next.has(PARAM)) return;
  const state = location.state;
  if (state !== null && typeof state === 'object' && 'cardSheet' in state && state.cardSheet === true) {
    return navigate(-1);
  }
  next.delete(PARAM);
  const search = next.toString();
  return navigate(
    { pathname: location.pathname, search: search ? `?${search}` : '' },
    { replace: true, preventScrollReset: true, state },
  );
}


export function useCardSheet() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const raw = params.get(PARAM);
  const openId = raw && /^\d+$/.test(raw) ? Number(raw) : null;

  const open = useCallback(
    (characterId: number) => openCardSheet(navigate, location, characterId),
    [location, navigate],
  );

  const close = useCallback(() => closeCardSheet(navigate, location), [location, navigate]);

  return { openId, open, close };
}
